import moment from "moment";

import PackageDetailApi from "issara-sdk/apis/PackageDetail_core";
import PackageList from "issara-sdk/apis/PackageList_core";
import PackageOrderDetail from "issara-sdk/apis/PackageOrderDetail_core";
import PackageOrderItemBinderToRealOrderView from "issara-sdk/apis/PackageOrderItemBinderToRealOrderView_core";
import PackageOrderList from "issara-sdk/apis/PackageOrderList_core";
import PackageOrderWithItemList from "issara-sdk/apis/PackageOrderWithItemList_core";
import PackagesOptimizeView from "issara-sdk/apis/PackagesOptimizeView_core";
import PackageUsageHistoryList from "issara-sdk/apis/PackageUsageHistoryList_apps_APP";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";
import type { OptimizePackageSelectionType } from "react-lib/apps/HISV3/HCU/sequence/PackagePurchase";
import { beToAd } from "react-lib/utils/dateUtils";

import {
  binderErrorOf,
  binderItemsOf,
  optimizeErrorOf,
  PACKAGE_PAGE_SIZE,
  type PackageDetail,
  type PackageItem,
  type PackageOrder,
  type PackageUsageLog,
} from "../../patient/packageModelV4";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  LOAD: "LOAD",
  SEARCH_SALE: "SEARCH_SALE",
  DETAIL: "DETAIL",
  ADD: "ADD",
  UPDATE_QTY: "UPDATE_QTY",
  REMOVE: "REMOVE",
  LOAD_ORDERS: "LOAD_ORDERS",
  OPTIMIZE: "OPTIMIZE",
  ORDER: "ORDER",
} as const;

export const SEQUENCE_NAME = "PackageV4" as const;

export const CARD_NAME = "CardPackageV4" as const;

/** =============== Types and Interfaces ===============  */
export type SaleFilter = { keyword: string; serviceType: string; packageType: string };

export type State = Partial<{
  PackageV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    patientId: number;
    filter: SaleFilter;
    page: number;
    saleItems: PackageItem[];
    saleTotal: number;
    detail: PackageDetail | null;
    orders: PackageOrder[];
    usageLogs: PackageUsageLog[];
    optimize: { orderIds: number[]; packages: OptimizePackageSelectionType[] } | null;
  }> | null;
}>;

export type PickedState = PickMainState<"buttonLoadCheck" | "errorMessage">;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "GetMasterData"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.LOAD]: { patientId: number };
  [ACTIONS.SEARCH_SALE]: { filter: SaleFilter; page: number };
  [ACTIONS.DETAIL]: { packageId: number };
  [ACTIONS.ADD]: { item: PackageItem; onDone?: (added: boolean) => void };
  [ACTIONS.UPDATE_QTY]: { order: PackageOrder; quantity: number };
  [ACTIONS.REMOVE]: { order: PackageOrder };
  [ACTIONS.LOAD_ORDERS]: { patientId?: number };
  [ACTIONS.OPTIMIZE]: { orderIds: number[] };
  [ACTIONS.ORDER]: { orderIds: number[]; onDone?: () => void };
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type ApiResult = [any, any, any?];

/** ===================== Constants ====================  */
export const StateInitial: State = {
  PackageV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

export const EMPTY_SALE_FILTER: SaleFilter = { keyword: "", serviceType: "", packageType: "" };

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: [
        ["packageType", {}],
        ["packageServiceType", {}],
      ],
    },
  });

  controller.setState(
    {
      PackageV4Sequence: {
        sequenceIndex: "Action",
        filter: EMPTY_SALE_FILTER,
        page: 1,
        saleItems: [],
        saleTotal: 0,
        detail: null,
        orders: [],
        usageLogs: [],
        optimize: null,
      },
    },
    () => {
      if ("action" in params) {
        Action(controller, params as unknown as ActionType);
      }
    },
  );
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.LOAD]: HandleLoad,
    [ACTIONS.SEARCH_SALE]: HandleSearchSale,
    [ACTIONS.DETAIL]: HandleDetail,
    [ACTIONS.ADD]: HandleAdd,
    [ACTIONS.UPDATE_QTY]: HandleUpdateQty,
    [ACTIONS.REMOVE]: HandleRemove,
    [ACTIONS.LOAD_ORDERS]: HandleLoadOrders,
    [ACTIONS.OPTIMIZE]: HandleOptimize,
    [ACTIONS.ORDER]: HandleOrder,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleLoad: Handler<Params<Actions["LOAD"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState(
    {
      PackageV4Sequence: {
        ...state.PackageV4Sequence,
        patientId: params.patientId,
        orders: [],
        usageLogs: [],
        optimize: null,
      },
    },
    () => {
      HandleSearchSale(controller, {
        ...params,
        action: ACTIONS.SEARCH_SALE,
        filter: EMPTY_SALE_FILTER,
        page: 1,
      });
      HandleLoadOrders(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.LOAD_ORDERS });
    },
  );
};

const HandleSearchSale: Handler<Params<Actions["SEARCH_SALE"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    PackageV4Sequence: { ...state.PackageV4Sequence, filter: params.filter, page: params.page },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH_SALE]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.SEARCH_SALE]: null },
  });

  const [res, error]: ApiResult = await PackageList.list({
    apiToken: controller.apiToken,
    params: {
      active: true,
      keyword: params.filter.keyword || undefined,
      package_service_type: params.filter.serviceType || undefined,
      package_type: params.filter.packageType || undefined,
      patient: state.PackageV4Sequence?.patientId,
      limit: PACKAGE_PAGE_SIZE,
      offset: (params.page - 1) * PACKAGE_PAGE_SIZE,
      include_affiliate_exclusive: true,
      of_division: controller.data.division,
    },
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.SEARCH_SALE, error });

    return;
  }

  const now = moment();
  const items: PackageItem[] = (res?.items ?? []).filter((item: PackageItem) => {
    const start = item.start_date ? beToAd(item.start_date) : null;
    const end = item.end_date ? beToAd(item.end_date) : null;

    return (!start || now.isSameOrAfter(start, "day")) && (!end || now.isSameOrBefore(end, "day"));
  });

  state = controller.getState();

  controller.setState({
    PackageV4Sequence: {
      ...state.PackageV4Sequence,
      saleItems: items,
      saleTotal: res?.total ?? items.length,
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.SEARCH_SALE]: BLC_STATUS.SUCCESS },
  });
};

const HandleDetail: Handler<Params<Actions["DETAIL"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    PackageV4Sequence: { ...state.PackageV4Sequence, detail: null },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DETAIL]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.DETAIL]: null },
  });

  const [res, error]: ApiResult = await PackageDetailApi.retrieve({
    apiToken: controller.apiToken,
    pk: params.packageId,
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.DETAIL, error });

    return;
  }

  state = controller.getState();

  controller.setState({
    PackageV4Sequence: { ...state.PackageV4Sequence, detail: res ?? null },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DETAIL]: BLC_STATUS.SUCCESS },
  });
};

/** เพิ่มแพ็กเกจเข้ารายการที่เลือก: มีแถวที่ยังไม่ชำระของแพ็กเกจนี้อยู่แล้วจะไม่สร้างซ้ำ */
const HandleAdd: Handler<Params<Actions["ADD"]>> = async (controller, params) => {
  let state = controller.getState();
  const patientId = state.PackageV4Sequence?.patientId;
  const btnKey = `${BTN_ACTS.ADD}_${params.item.id}`;

  if (!patientId) return;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.ADD]: null },
  });

  const [orders, listError] = await GetOrders(controller, { patientId, statuses: ["BILLED"] });
  const exists = !listError && orders.some((order) => order.product === params.item.id);

  let error = listError;

  if (!error && !exists) {
    [, error] = await PackageOrderList.create({
      apiToken: controller.apiToken,
      data: { patient: patientId, product: params.item.id, quantity: 1 },
      extra: { division: controller.data.division },
    });
  }

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: btnKey, errorKey: BTN_ACTS.ADD, error });

    return;
  }

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [btnKey]: null },
  });

  params.onDone?.(!exists);

  if (!exists) {
    await HandleLoadOrders(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.LOAD_ORDERS });
  }
};

const HandleUpdateQty: Handler<Params<Actions["UPDATE_QTY"]>> = async (controller, params) => {
  await SaveOrder(controller, {
    ...params,
    btnAction: `${BTN_ACTS.UPDATE_QTY}_${params.order.id}`,
    data: { quantity: params.quantity },
  });
};

const HandleRemove: Handler<Params<Actions["REMOVE"]>> = async (controller, params) => {
  await SaveOrder(controller, {
    ...params,
    btnAction: `${BTN_ACTS.REMOVE}_${params.order.id}`,
    data: { active: false, quantity: params.order.quantity },
  });
};

const HandleLoadOrders: Handler<Params<Actions["LOAD_ORDERS"]>> = async (controller, params) => {
  let state = controller.getState();
  const patientId = state.PackageV4Sequence?.patientId;

  if (!patientId) return;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_ORDERS]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.LOAD_ORDERS]: null },
  });

  const [[orders, error], [logs]] = await Promise.all([
    GetOrders(controller, { patientId, statuses: ["BILLED", "PAID"] }),
    PackageUsageHistoryList.get({
      apiToken: controller.apiToken,
      params: { patient: patientId },
      extra: { division: controller.data.division },
    }) as Promise<ApiResult>,
  ]);

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.LOAD_ORDERS, error });

    return;
  }

  state = controller.getState();

  controller.setState({
    PackageV4Sequence: {
      ...state.PackageV4Sequence,
      orders,
      usageLogs: logs?.items ?? [],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD_ORDERS]: BLC_STATUS.SUCCESS },
  });
};

/** แพ็กเกจที่ครอบคลุมรายการที่เลือกในราคาที่ถูกกว่า (core/package/optimize/) */
const HandleOptimize: Handler<Params<Actions["OPTIMIZE"]>> = async (controller, params) => {
  let state = controller.getState();
  const orders = (state.PackageV4Sequence?.orders ?? []).filter((order) =>
    params.orderIds.includes(order.id),
  );

  controller.setState({
    PackageV4Sequence: { ...state.PackageV4Sequence, optimize: null },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.OPTIMIZE]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.OPTIMIZE]: null },
  });

  const [res, error]: ApiResult = await PackagesOptimizeView.post({
    apiToken: controller.apiToken,
    data: {
      input_packages: orders.map((order) => ({
        package: order.product,
        quantity: Number(order.quantity) || 1,
      })),
    },
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, {
      ...params,
      btnAction: BTN_ACTS.OPTIMIZE,
      error: optimizeErrorOf(error, orders),
    });

    return;
  }

  state = controller.getState();

  controller.setState({
    PackageV4Sequence: {
      ...state.PackageV4Sequence,
      optimize: { orderIds: params.orderIds, packages: res?.packages ?? [] },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.OPTIMIZE]: BLC_STATUS.SUCCESS },
  });
};

/** ส่งรายการคงเหลือของแพ็กเกจที่ชำระแล้วเป็นคำสั่งจริง (core binder-to-real-order) */
const HandleOrder: Handler<Params<Actions["ORDER"]>> = async (controller, params) => {
  let state = controller.getState();
  const orders = (state.PackageV4Sequence?.orders ?? []).filter((order) =>
    params.orderIds.includes(order.id),
  );

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ORDER]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.ORDER]: null },
  });

  const [, error]: ApiResult = await PackageOrderItemBinderToRealOrderView.post({
    apiToken: controller.apiToken,
    data: { patient: state.PackageV4Sequence?.patientId, items: binderItemsOf(orders) },
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, {
      ...params,
      btnAction: BTN_ACTS.ORDER,
      error: binderErrorOf(error, orders),
    });

    return;
  }

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.ORDER]: null },
  });

  params.onDone?.();

  await HandleLoadOrders(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.LOAD_ORDERS });
};

/** ====================================================  */
/**                          API                          */
/** ====================================================  */
const GetOrders = async (
  controller: Parameters<Handler>[0],
  params: { patientId: number; statuses: string[] },
): Promise<[PackageOrder[], any]> => {
  const [res, error]: ApiResult = await PackageOrderWithItemList.get({
    apiToken: controller.apiToken,
    params: { patient: params.patientId, statuses: params.statuses, active: true, limit: 100 },
    extra: { division: controller.data.division },
  });

  const orders: PackageOrder[] = (res?.items ?? []).filter(
    (order: PackageOrder) => order.active !== false,
  );

  return [orders, error];
};

const SaveOrder: Handler<{
  order: PackageOrder;
  btnAction: string;
  data: Record<string, unknown>;
}> = async (controller, params) => {
  let state = controller.getState();
  const patientId = state.PackageV4Sequence?.patientId;

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [params.btnAction]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.LOAD_ORDERS]: null },
  });

  const [, error]: ApiResult = await PackageOrderDetail.update({
    apiToken: controller.apiToken,
    pk: params.order.id,
    data: { patient: patientId, product: params.order.product, ...params.data },
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, errorKey: BTN_ACTS.LOAD_ORDERS, error });

    return;
  }

  state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [params.btnAction]: null },
  });

  await HandleLoadOrders(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.LOAD_ORDERS });
};
